import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Copy,
  Eye,
  EyeOff,
  Download,
  Facebook,
  Twitter,
  Linkedin,
  Github,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import Footer from "../../../components/Footer";
import ToolsFooter from "../ToolsFooter";

export default function TempPasswordPage() {
  const { id } = useParams();
  const [password, setPassword] = useState("");
  const [remaining, setRemaining] = useState(0);
  const [expired, setExpired] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!id) return setExpired(true);
    const stored = JSON.parse(localStorage.getItem("tempPasswords") || "{}");
    const data = stored[id];
    if (!data) return setExpired(true);

    const timeLeft = Math.floor((data.expiry - Date.now()) / 1000);
    if (timeLeft <= 0) {
      delete stored[id];
      localStorage.setItem("tempPasswords", JSON.stringify(stored));
      return setExpired(true);
    }

    setPassword(data.password);
    setRemaining(timeLeft);

    const timer = setInterval(() => {
      const newTime = Math.floor((data.expiry - Date.now()) / 1000);
      if (newTime <= 0) {
        clearInterval(timer);
        setExpired(true);
        setPassword("");
        delete stored[id];
        localStorage.setItem("tempPasswords", JSON.stringify(stored));
      } else {
        setRemaining(newTime);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [id]);

  const formatTime = (sec) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  };

  const copyPassword = () => {
    navigator.clipboard.writeText(password);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadPassword = () => {
    const blob = new Blob([password], { type: "text/plain;charset=utf-8" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "temporary-password.txt";
    link.click();
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      {/* Main content */}
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="bg-white p-6 rounded-2xl shadow-lg w-full max-w-md text-center">
          {!expired ? (
            <>
              <h2 className="text-xl font-bold mb-4">Temporary Password</h2>

              {/* Password display */}
              <div className="flex items-center justify-center gap-2 mb-2">
                <span className="font-mono text-lg">
                  {showPassword ? password : "••••••••••••"}
                </span>
                <button
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="text-gray-600 hover:text-gray-800"
                  title={showPassword ? "Hide Password" : "Show Password"}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
                <button
                  onClick={copyPassword}
                  className="text-gray-600 hover:text-gray-800"
                  title="Copy Password"
                >
                  <Copy size={20} />
                </button>
                {copied && (
                  <span className="text-green-600 text-sm ml-1">Copied!</span>
                )}
              </div>

              {/* Expiry */}
              <p className="text-sm text-gray-700 mb-4">
                Expires in: {formatTime(remaining)}
              </p>

              {/* Download button */}
              <div className="flex justify-center gap-4 mb-4">
                <button
                  onClick={downloadPassword}
                  className="flex items-center gap-1 bg-blue-600 text-white px-3 py-1 rounded-lg hover:bg-blue-700 transition"
                >
                  <Download size={16} /> Download
                </button>
              </div>

              {/* QR code */}
              <div className="flex justify-center">
                <QRCodeSVG value={password} size={128} />
              </div>
            </>
          ) : (
            <h2 className="text-xl font-bold text-red-600">
              This temporary password has expired!
            </h2>
          )}
        </div>
      </div>



    </div>
  );
}
